import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../../lib/hooks/useAuth';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Route wrapper that protects pages requiring authentication.
 * Redirects to login page if user is not authenticated.
 * Shows loading state during authentication check.
 *
 * @example
 * ```tsx
 * <Route
 *   path="/chat/:id"
 *   element={
 *     <ProtectedRoute>
 *       <ChatPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, error } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth status
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">Verifica autenticazione...</p>
        </div>
      </div>
    );
  }

  // Show error state if authentication check failed
  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="max-w-md rounded-lg border border-destructive/50 bg-destructive/10 p-6">
          <h2 className="mb-2 text-lg font-semibold text-destructive">Errore di Autenticazione</h2>
          <p className="text-sm text-destructive/80">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  // Redirect to login page if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
}
